import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

const ITEMS = '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]';

/**
 * Клавіатура випадного меню (patterns/keyboard, foundations/accessibility): після відкриття фокус — на вибраному (aria-checked /
 * aria-current) або першому пункті; ↑ ↓ Home End — між пунктами по колу; Esc — закрити й повернути фокус кнопці меню; Tab — закрити.
 */
export function useMenuKeys<T extends HTMLElement>(open: boolean, onClose: () => void, trigger: RefObject<HTMLElement | null>) {
  const menu = useRef<T>(null);
  useEffect(() => {
    if (!open || !menu.current) return;
    const selected = menu.current.querySelector<HTMLElement>('[aria-checked="true"], [aria-current="page"]');
    (selected ?? menu.current.querySelector<HTMLElement>(ITEMS))?.focus({ preventScroll: true });
  }, [open]);
  const onKeyDown = useCallback((e: KeyboardEvent) => {
    const items = Array.from(menu.current?.querySelectorAll<HTMLElement>(ITEMS) ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => { e.preventDefault(); items[(i + items.length) % items.length]?.focus(); };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at < 0 ? items.length - 1 : at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); trigger.current?.focus(); }
    else if (e.key === 'Tab') onClose();
  }, [onClose, trigger]);
  return { menu, onKeyDown };
}
